import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button, classes } from './ui';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

/**
 * Custom select matching the marketplace chrome — native `<select>` menus are left unused so
 * the dark theme and focus ring stay consistent across browsers.
 */
export function Select({
  id,
  value,
  options,
  onChange,
  placeholder,
  className,
  disabled = false,
  'aria-label': ariaLabel,
}: {
  id?: string;
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={classes('relative', className)}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        className={classes(
          'input flex w-full items-center justify-between gap-3 text-left',
          open ? 'border-brand/50 ring-1 ring-brand/30' : '',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        )}
      >
        <span className={selected === undefined ? 'text-slate-500' : 'text-slate-100'}>
          {selected?.label ?? placeholder ?? 'Select…'}
        </span>
        <span aria-hidden="true" className="text-slate-500">
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-40 mt-1.5 max-h-64 w-full animate-fade-in overflow-y-auto overscroll-contain rounded-xl border border-slate-700/90 bg-surface-1/95 py-1 shadow-panel backdrop-blur-xl [scrollbar-gutter:stable]"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <li key={option.value === '' ? '__empty' : option.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  className={classes(
                    'flex w-full items-center px-3.5 py-2.5 text-left text-sm transition-colors',
                    isSelected
                      ? 'bg-brand/15 font-medium text-brand-soft'
                      : 'text-slate-200 hover:bg-slate-800/90',
                  )}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** Compact toggle used instead of native checkboxes in filters and settings. */
export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  id,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  id?: string;
}): JSX.Element {
  const generatedId = useId();
  const switchId = id ?? generatedId;

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <label htmlFor={switchId} className="cursor-pointer text-sm font-medium text-slate-200">
          {label}
        </label>
        {description === undefined ? null : (
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{description}</p>
        )}
      </div>
      <button
        type="button"
        id={switchId}
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => {
          if (!disabled) onChange(!checked);
        }}
        className={classes(
          'relative mt-0.5 h-6 w-11 shrink-0 rounded-full border transition-colors duration-200 ease-soft',
          checked
            ? 'border-brand/50 bg-brand shadow-glow'
            : 'border-slate-600 bg-slate-800 hover:border-slate-500',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        )}
      >
        <span
          aria-hidden="true"
          className={classes(
            'absolute top-0.5 left-0.5 h-4.5 w-4.5 rounded-full bg-white shadow transition-transform duration-200 ease-soft',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
          style={{ height: '1.125rem', width: '1.125rem' }}
        />
      </button>
    </div>
  );
}

/**
 * In-app confirmation panel — replaces browser `confirm()` so copy and styling stay on-brand.
 *
 * Pass `confirmText` for GitHub-style typed confirmation: the destructive button stays disabled
 * until the user types the exact phrase (typically a name).
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmText,
  confirmTextLabel,
  tone = 'danger',
  isLoading = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When set, confirm stays disabled until this exact string is typed. */
  confirmText?: string;
  /** Override for the typed-confirmation field label. */
  confirmTextLabel?: ReactNode;
  tone?: 'danger' | 'primary';
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const requiresTypedMatch = confirmText !== undefined && confirmText !== '';
  const confirmed = !requiresTypedMatch || typed === confirmText;

  useEffect(() => {
    if (!open) {
      setTyped('');
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isLoading) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, isLoading, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/70 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isLoading) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="panel w-full max-w-md animate-fade-in"
      >
        <h2 id="confirm-title" className="font-display text-lg font-bold text-slate-50">
          {title}
        </h2>
        <div className="mt-3 text-sm leading-relaxed text-slate-400">{description}</div>

        {requiresTypedMatch ? (
          <div className="mt-5">
            <label className="label" htmlFor={inputId}>
              {confirmTextLabel ?? (
                <>
                  Type <span className="font-mono text-slate-200">{confirmText}</span> to confirm
                </>
              )}
            </label>
            <input
              id={inputId}
              value={typed}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              disabled={isLoading}
              onChange={(event) => setTyped(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && confirmed && !isLoading) onConfirm();
              }}
              className="input font-mono"
              placeholder={confirmText}
            />
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            isLoading={isLoading}
            disabled={!confirmed}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Compact switch row for dense filter lists. */
export function SwitchRow({
  checked,
  onChange,
  label,
  disabled = false,
  className,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
      className={classes(
        'flex w-full items-center justify-between gap-3 rounded-xl px-2 py-2 text-left text-sm transition-colors',
        checked ? 'bg-slate-800/60 text-slate-100' : 'text-slate-300 hover:bg-slate-800/40',
        disabled ? 'cursor-not-allowed opacity-50' : '',
        className,
      )}
    >
      <span>{label}</span>
      <span
        aria-hidden="true"
        className={classes(
          'relative h-5 w-9 shrink-0 rounded-full border transition-colors',
          checked ? 'border-brand/40 bg-brand' : 'border-slate-600 bg-slate-900',
        )}
      >
        <span
          className={classes(
            'absolute top-0.5 left-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0',
          )}
        />
      </span>
    </button>
  );
}
